import React, { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import debounce from 'lodash.debounce'
import CustomIcon from 'src/components/CustomIcon'

const ConversationInput = ({
  className,
  id,
  name,
  placeholder,
  disabled,
  value: controlledValue,
  onChange,
  onSubmit = () => {}
}) => {
  const [localValue, setLocalValue] = useState(controlledValue || '')
  const draftValueRef = useRef(localValue)
  const [submitting, setSubmitting] = useState(false)
  const submittingRef = useRef(false)
  const mountedRef = useRef(true)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange
  const lastPublishedValueRef = useRef(controlledValue)
  const persistDraft = useMemo(
    () =>
      debounce(value => {
        lastPublishedValueRef.current = value
        if (onChangeRef.current) onChangeRef.current(value)
      }, 250),
    []
  )
  useEffect(() => {
    if (
      controlledValue !== undefined &&
      controlledValue !== lastPublishedValueRef.current
    ) {
      lastPublishedValueRef.current = controlledValue
      persistDraft.cancel()
      draftValueRef.current = controlledValue
      setLocalValue(controlledValue)
    }
  }, [controlledValue, persistDraft])
  useEffect(
    () => () => {
      persistDraft.flush()
      persistDraft.cancel()
      mountedRef.current = false
    },
    [persistDraft]
  )
  const controlled = controlledValue !== undefined
  const value = localValue
  const inputDisabled = disabled || submitting
  const _handleSubmit = async e => {
    e.preventDefault()
    const submittedValue = draftValueRef.current
    if (inputDisabled || submittingRef.current || !submittedValue.trim()) return
    if (controlled) {
      persistDraft.cancel()
      draftValueRef.current = ''
      lastPublishedValueRef.current = ''
      if (onChangeRef.current) onChangeRef.current('')
      setLocalValue('')
      onSubmit(submittedValue)
      return
    }
    submittingRef.current = true
    setSubmitting(true)
    try {
      const result = await onSubmit(submittedValue)
      if (mountedRef.current && result !== false) {
        draftValueRef.current = ''
        setLocalValue('')
      }
    } catch (error) {
      // Keep the reply available if its sender reports a failure.
    } finally {
      submittingRef.current = false
      if (mountedRef.current) setSubmitting(false)
    }
  }

  return (
    <div className={className}>
      <form onSubmit={_handleSubmit}>
        <Input
          disabled={inputDisabled}
          id={id}
          name={name}
          value={value}
          onChange={e => {
            const nextValue = e.target.value
            draftValueRef.current = nextValue
            setLocalValue(nextValue)
            if (controlled) persistDraft(nextValue)
          }}
          onBlur={() => persistDraft.flush()}
          placeholder={placeholder}
        />
        <SubmitButton
          type='submit'
          disabled={inputDisabled || !value.trim()}
          aria-label='Send message'
        >
          <StyledCustomIcon
            name='down'
            size={20}
            disabled={inputDisabled || !value.trim()}
          />
        </SubmitButton>
      </form>
    </div>
  )
}

export default styled(ConversationInput)`
  display: flex;
  flex-direction: row;
  width: 100%;
  box-sizing: border-box;
  height: 42px;
  align-items: center;
  border-radius: 4px;
  border: solid 1px rgba(198, 203, 212, 0.3);
  background-color: #f9fafb;
  border: 1px solid ${({ theme }) => theme.colors.iron.hex()};
  padding: 0 0.6rem;
  form {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`

const Input = styled.input`
  flex: 1;
  background-color: transparent;
  border: unset;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
  outline: none;
  &::placeholder {
    display: inline-block;
    font-size: 0.875rem;
    color: ${({ theme }) => theme.colors.black.alpha(0.5).hex()};
  }
`

const SubmitButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  background: none;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;
  padding: 0;

  &:hover:not(:disabled) {
    transform: scale(1.1);
  }

  &:active:not(:disabled) {
    transform: scale(0.9);
  }
`

const StyledCustomIcon = styled(CustomIcon)`
  transform: rotate(270deg);
  transition: all 0.2s ease;
  color: ${({ disabled, theme }) =>
    disabled ? theme.colors.ghost.hex() : theme.colors.apple.hex()};

  ${SubmitButton}:hover:not(:disabled) & {
    color: ${({ theme }) => theme.colors.apple.darken(0.1).hex()};
  }
`
